import { ADMIN_FUNCTIONS } from '../config/functionRoutes';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';

export interface MOVolunteerUserOption {
  userId: string;
  personId?: number | null;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string | null;
  yearOfBirth?: number | null;
  gender?: 'male' | 'female' | '';
  representing?: string[];
}

export interface MOVolunteerEntry extends MOVolunteerUserOption {
  id: string;
  editionId: string;
  editionYear: number | null;
}

export interface MOVolunteerPayload {
  operationKey?: string;
  createPerson?: boolean;
  resultId?: string | null;
  editionId: string;
  editionYear: number;
  userId?: string | null;
  personId?: number | null;
  firstName: string;
  lastName: string;
  displayName?: string | null;
  email?: string | null;
  yearOfBirth?: number | null;
  gender?: 'male' | 'female' | '';
  representing?: string[] | string | null;
}

export interface ManageMOVolunteerResult {
  resultId?: string;
  userId?: string;
  personId?: number;
  deleted?: boolean;
}

const toNumberOrNull = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeString = (value: unknown): string => String(value ?? '').trim();

const normalizeSearchName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s'-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeGender = (value: unknown): 'male' | 'female' | '' => {
  const v = normalizeString(value).toLowerCase();
  if (v === 'male' || v === 'm' || v === 'menn') return 'male';
  if (v === 'female' || v === 'f' || v === 'kvinner') return 'female';
  return '';
};

const normalizeRepresenting = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeString(item)).filter(Boolean);
  }
  const single = normalizeString(value);
  return single ? [single] : [];
};

const splitDisplayName = (displayName: string): { firstName: string; lastName: string } => {
  const parts = normalizeString(displayName).split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const getDisplayName = (data: Record<string, unknown>): string => {
  const displayName = normalizeString(data.displayName);
  if (displayName) return displayName;
  return `${normalizeString(data.firstName)} ${normalizeString(data.lastName)}`.trim();
};

const mapUserOption = (id: string, data: Record<string, unknown>): MOVolunteerUserOption => {
  const displayName = getDisplayName(data);
  const fallback = splitDisplayName(displayName);
  return {
    userId: id,
    personId: toNumberOrNull(data.personId),
    firstName: normalizeString(data.firstName) || fallback.firstName,
    lastName: normalizeString(data.lastName) || fallback.lastName,
    displayName,
    email: normalizeString(data.email) || null,
    yearOfBirth: toNumberOrNull(data.yearOfBirth),
    gender: normalizeGender(data.gender),
    representing: normalizeRepresenting(data.representing),
  };
};

const mapPublicProfileOption = (id: string, data: Record<string, unknown>): MOVolunteerUserOption => {
  const option = mapUserOption(id, data);
  return {
    ...option,
    userId: '',
    personId: toNumberOrNull(data.personId) ?? toNumberOrNull(id),
  };
};

export const searchMoVolunteerUsers = async (searchText: string): Promise<MOVolunteerUserOption[]> => {
  const lookup = normalizeSearchName(searchText);
  if (lookup.length < 2) {
    return [];
  }

  const usersRef = collection(db, 'users');
  const bySearchName = await getDocs(
    query(
      usersRef,
      where('searchFullName', '>=', lookup),
      where('searchFullName', '<=', `${lookup}\uf8ff`),
      orderBy('searchFullName'),
      limit(8)
    )
  );

  const results = new Map<string, MOVolunteerUserOption>();
  bySearchName.docs.forEach((docSnap) => {
    results.set(docSnap.id, mapUserOption(docSnap.id, docSnap.data()));
  });

  const publicProfilesRef = collection(db, 'publicProfiles');
  const publicProfiles = await getDocs(query(publicProfilesRef, limit(2000)));
  publicProfiles.docs.forEach((docSnap) => {
    if (results.size >= 8) return;
    const option = mapPublicProfileOption(docSnap.id, docSnap.data());
    if (normalizeSearchName(option.displayName).includes(lookup)) {
      results.set(`person-${option.personId ?? docSnap.id}`, option);
    }
  });

  return Array.from(results.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName, 'nb', { sensitivity: 'base' })
  );
};

export const getMoVolunteersForEdition = async (editionId: string): Promise<MOVolunteerEntry[]> => {
  const resultsRef = collection(db, 'moResults');
  const snapshot = await getDocs(query(resultsRef, where('editionId', '==', editionId)));

  return snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data();
      const resultClass = normalizeString(data.class || data.participationType).toLowerCase();
      if (resultClass !== 'volunteer' && !resultClass.includes('funksjon')) {
        return null;
      }
      const userOption = mapUserOption(normalizeString(data.userId), data);
      if (!userOption.displayName && data.personId != null) {
        return null;
      }
      return {
        ...userOption,
        id: docSnap.id,
        editionId: normalizeString(data.editionId),
        editionYear: toNumberOrNull(data.editionYear),
      } as MOVolunteerEntry;
    })
    .filter((item): item is MOVolunteerEntry => item !== null)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'nb', { sensitivity: 'base' }));
};

export const enrichMoVolunteerFromPublicProfile = async (
  option: MOVolunteerUserOption
): Promise<MOVolunteerUserOption> => {
  if (!option.personId) {
    return option;
  }
  const profileSnap = await getDoc(doc(db, 'publicProfiles', String(option.personId)));
  if (!profileSnap.exists()) {
    return option;
  }
  return {
    ...option,
    ...mapPublicProfileOption(profileSnap.id, profileSnap.data()),
    userId: option.userId,
  };
};

export const upsertMoVolunteer = async (
  volunteer: MOVolunteerPayload
): Promise<ManageMOVolunteerResult> => {
  const functions = getFunctions(undefined, 'europe-west1');
  const callable = httpsCallable<
    { action: 'upsert'; volunteer: MOVolunteerPayload },
    ManageMOVolunteerResult
  >(functions, ADMIN_FUNCTIONS.volunteer);
  const result = await callable({ action: 'upsert', volunteer });
  return result.data;
};

export const deleteMoVolunteer = async (resultId: string): Promise<ManageMOVolunteerResult> => {
  const functions = getFunctions(undefined, 'europe-west1');
  const callable = httpsCallable<
    { action: 'delete'; resultId: string },
    ManageMOVolunteerResult
  >(functions, ADMIN_FUNCTIONS.volunteer);
  const result = await callable({ action: 'delete', resultId });
  return result.data;
};
